import { useGetDirectusMeQuery } from '@/features/api/endpoints/userEndpoints';
import type { DirectusUsers } from '@/models/types';

export type UseDirectusUserResult =
  | { status: 'loading'; user: undefined }
  | { status: 'unauthenticated'; user: undefined }
  | { status: 'error'; user: undefined }
  | { status: 'success'; user: DirectusUsers };

/**
 * Retourne l'utilisateur Directus courant (`/users/me`).
 * La session Directus (cookie) est la source de vérité : on ne dépend pas de l'état du token
 * OIDC, qui peut expirer indépendamment alors que la session Directus reste valide.
 */
export const useGetCurrentUser = (): UseDirectusUserResult => {
  const { data, isLoading, isError } = useGetDirectusMeQuery();

  if (isLoading) {
    return { status: 'loading', user: undefined };
  }

  if (isError || !data) {
    return { status: 'error', user: undefined };
  }

  return { status: 'success', user: data };
};

export const useIsAdmin = () => {
  const { user } = useGetCurrentUser();

  if (!user) {
    return false;
  }

  return user.role === import.meta.env.VITE_DIRECTUS_ADMIN_ROLE;
};

export const useIsUser = () => {
  const { user } = useGetCurrentUser();

  if (!user) {
    return false;
  }

  return user.role === import.meta.env.VITE_DIRECTUS_USER_ROLE;
};

export const useIsAdminOrUser = () => {
  const { data, isLoading, isError } = useGetDirectusMeQuery();

  const value =
    data?.role === import.meta.env.VITE_DIRECTUS_ADMIN_ROLE ||
    data?.role === import.meta.env.VITE_DIRECTUS_USER_ROLE;
  return {
    data,
    isLoading,
    isError,
    value,
  };
};
